import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  HelpCircle, FileCheck, Search, Plus, Upload, ArrowLeft, CheckCircle2, XCircle, Eye, Trash2,
  ClipboardList, Users, Calendar, ArrowRight, Filter, ClipboardCheck, ShieldCheck, FileText,
} from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard } from "@/components/StatCard";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingBlock, EmptyState } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const QUESTION_TYPES = [
  { value: "single_choice", label: "Single Choice MCQ" },
  { value: "multi_select", label: "Multi-Select MCQ" },
  { value: "true_false", label: "True / False" },
  { value: "short_answer", label: "Short Answer" },
  { value: "long_answer", label: "Long Answer" },
];
const DIFFICULTIES = [
  { value: "easy", label: "Easy" },
  { value: "medium", label: "Medium" },
  { value: "difficult", label: "Difficult" },
];

// =========================================================================
// EXAM DASHBOARD
// =========================================================================
export function AdminExamDashboardPage() {
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/exam-dashboard");
        setTotals(data.data.totals);
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setLoading(false); }
    })();
  }, []);
  if (loading) return <DashboardLayout title="Exam overview"><LoadingBlock label="Loading dashboard…" /></DashboardLayout>;
  if (!totals) return null;
  return (
    <DashboardLayout title="Examination Engine" breadcrumbs={["Super Admin", "Exams"]}>
      <div className="space-y-6" data-testid="admin-exam-dashboard">
        <section className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Total exams" value={totals.total_exams} icon="FileCheck" tone="navy" testId="exam-kpi-total" index={0} />
          <StatCard label="Published" value={totals.published_exams} icon="CircleCheckBig" tone="green" testId="exam-kpi-published" index={1} />
          <StatCard label="Total questions" value={totals.total_questions} icon="HelpCircle" tone="navy" testId="exam-kpi-questions" index={2} />
          <StatCard label="Approved questions" value={totals.approved_questions} icon="ShieldCheck" tone="green" testId="exam-kpi-approved" index={3} />
        </section>
        <section className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Total attempts" value={totals.total_attempts} icon="ClipboardList" tone="slate" testId="exam-kpi-attempts" index={4} />
          <StatCard label="Passed" value={totals.passed} icon="CircleCheckBig" tone="green" testId="exam-kpi-passed" index={5} />
          <StatCard label="Failed" value={totals.failed} icon="X" tone="rose" testId="exam-kpi-failed" index={6} />
          <StatCard label="Average score" value={`${totals.average_score}%`} icon="TrendingUp" tone="navy" testId="exam-kpi-avg" index={7} />
        </section>
        <div className="grid gap-4 sm:grid-cols-4">
          <QuickLink to="/app/super-admin/question-bank" icon={HelpCircle} title="Question Bank" desc="Add, import, review questions" />
          <QuickLink to="/app/super-admin/exams" icon={FileCheck} title="Exams" desc="Create and publish exams" />
          <QuickLink to="/app/super-admin/exam-results" icon={ClipboardList} title="Results" desc="Attempt-wise results" />
          <QuickLink to="/app/super-admin/question-bank/import" icon={Upload} title="Import Questions" desc="Bulk CSV / XLSX upload" />
        </div>
      </div>
    </DashboardLayout>
  );
}

function QuickLink({ to, icon: Icon, title, desc }) {
  return (
    <Link to={to} className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-[#101876]/40 hover:shadow-md transition" data-testid={`quicklink-${title.toLowerCase().replace(/\s+/g,"-")}`}>
      <Icon className="h-5 w-5 text-[#101876]" />
      <div className="mt-3 font-bold text-sm">{title}</div>
      <div className="text-xs text-slate-500">{desc}</div>
    </Link>
  );
}

// =========================================================================
// QUESTION BANK LIST (admin + faculty)
// =========================================================================
export function QuestionBankListPage({ scope = "admin" }) {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [filters, setFilters] = useState({ course_id: "all", status: "all", question_type: "all", difficulty: "all", search: "" });

  useEffect(() => {
    (async () => {
      const endpoint = scope === "admin" ? "/admin/courses" : "/faculty/my-courses";
      try {
        const { data } = await api.get(endpoint, { params: scope === "admin" ? { page_size: 500, status: "published" } : {} });
        const list = scope === "admin" ? (data.data.courses || []) : (data.data.courses || []);
        setCourses(list);
      } catch { /* noop */ }
    })();
  }, [scope]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
      const { data } = await api.get(`/${scope}/question-bank`, { params });
      setRows(data.data.questions);
      setTotal(data.data.total);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [scope, page, pageSize, filters]);
  useEffect(() => { load(); }, [load]);

  return (
    <DashboardLayout title="Question Bank" breadcrumbs={[scope === "admin" ? "Super Admin" : "Faculty", "Question Bank"]}>
      <div className="space-y-4" data-testid="question-bank-list">
        <div className="flex flex-wrap items-center gap-3">
          <Link to={scope === "admin" ? "/app/super-admin/question-bank/new" : "/app/faculty/question-bank/new"}
            className="rounded-md bg-[#101876] px-4 py-2 text-xs font-bold text-white hover:bg-[#0c135c] flex items-center gap-1" data-testid="btn-add-question">
            <Plus className="h-3.5 w-3.5" /> Add question
          </Link>
          <Link to={scope === "admin" ? "/app/super-admin/question-bank/import" : "/app/faculty/question-bank/import"}
            className="rounded-md border border-slate-300 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-1" data-testid="btn-import-questions">
            <Upload className="h-3.5 w-3.5" /> Import CSV
          </Link>
          <div className="ml-auto text-xs text-slate-500">Total: <span className="font-bold">{total}</span></div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 grid gap-3 sm:grid-cols-5">
          <div className="relative sm:col-span-2">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input placeholder="Search question text…" value={filters.search}
              onChange={(e) => { setFilters((p) => ({ ...p, search: e.target.value })); setPage(1); }}
              className="w-full rounded-md border border-slate-200 pl-9 pr-3 py-2 text-sm" data-testid="qb-search" />
          </div>
          <Select value={filters.course_id} onValueChange={(v) => { setFilters((p) => ({ ...p, course_id: v })); setPage(1); }}>
            <SelectTrigger data-testid="qb-course-filter"><SelectValue placeholder="Course" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All courses</SelectItem>
              {courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.course_code}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.status} onValueChange={(v) => { setFilters((p) => ({ ...p, status: v })); setPage(1); }}>
            <SelectTrigger data-testid="qb-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="pending_review">Pending Review</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="suspended">Suspended</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.difficulty} onValueChange={(v) => { setFilters((p) => ({ ...p, difficulty: v })); setPage(1); }}>
            <SelectTrigger data-testid="qb-difficulty-filter"><SelectValue placeholder="Difficulty" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All difficulties</SelectItem>
              {DIFFICULTIES.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {loading ? <LoadingBlock label="Loading questions…" /> : rows.length === 0 ? (
          <EmptyState title="No questions yet" description="Add or import questions to build your question bank." icon={HelpCircle} />
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/60 text-xs uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-left">Question</th>
                  <th className="px-4 py-3 text-left">Course</th>
                  <th className="px-4 py-3 text-left">Topic</th>
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-left">Difficulty</th>
                  <th className="px-4 py-3 text-left">Marks</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((q) => (
                  <tr key={q.id} className="hover:bg-slate-50/60" data-testid={`qb-row-${q.id}`}>
                    <td className="px-4 py-3 max-w-md">
                      <div className="font-medium text-slate-800 line-clamp-2">{q.question_text}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{q.course_code}</td>
                    <td className="px-4 py-3 text-xs">{q.topic || "—"}</td>
                    <td className="px-4 py-3 text-xs capitalize">{q.question_type.replace(/_/g, " ")}</td>
                    <td className="px-4 py-3 text-xs capitalize">{q.difficulty_level}</td>
                    <td className="px-4 py-3 text-xs">{q.marks}</td>
                    <td className="px-4 py-3"><StatusBadge status={q.status} /></td>
                    <td className="px-4 py-3 text-right">
                      <Link to={scope === "admin" ? `/app/super-admin/question-bank/${q.id}` : `/app/faculty/question-bank/${q.id}`}
                        className="text-xs font-bold text-[#101876] hover:underline" data-testid={`qb-view-${q.id}`}>View →</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {total > pageSize && (
          <div className="flex items-center justify-center gap-2 py-3">
            <button disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="rounded-md border border-slate-200 px-3 py-1.5 text-xs disabled:opacity-40">Previous</button>
            <span className="text-xs text-slate-500">Page {page} of {Math.ceil(total / pageSize)}</span>
            <button disabled={page * pageSize >= total} onClick={() => setPage((p) => p + 1)} className="rounded-md border border-slate-200 px-3 py-1.5 text-xs disabled:opacity-40">Next</button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

// =========================================================================
// QUESTION FORM (create/edit)
// =========================================================================
export function QuestionFormPage({ scope = "admin", mode = "create" }) {
  const nav = useNavigate();
  const { qid } = useParams();
  const [courses, setCourses] = useState([]);
  const [form, setForm] = useState({
    course_id: "", topic: "", sub_topic: "",
    question_type: "single_choice", question_text: "",
    explanation: "", difficulty_level: "medium",
    tags: "", marks: 1, negative_marks: 0, status: "draft",
    options: [
      { option_text: "", is_correct: false },
      { option_text: "", is_correct: false },
      { option_text: "", is_correct: false },
      { option_text: "", is_correct: false },
    ],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const endpoint = scope === "admin" ? "/admin/courses" : "/faculty/my-courses";
      try {
        const { data } = await api.get(endpoint, {
          params: scope === "admin" ? { page_size: 500, status_filter: "active" } : {},
        });
        setCourses(data.data.courses || []);
      } catch { /* */ }
    })();
    if (mode === "edit" && qid) {
      (async () => {
        try {
          const { data } = await api.get(`/${scope}/question-bank/${qid}`);
          const q = data.data.question;
          setForm({
            course_id: q.course_id, topic: q.topic || "", sub_topic: q.sub_topic || "",
            question_type: q.question_type, question_text: q.question_text,
            explanation: q.explanation || "", difficulty_level: q.difficulty_level,
            tags: (q.tags || []).join(","), marks: q.marks, negative_marks: q.negative_marks || 0,
            status: q.status,
            options: (q.options || []).map((o) => ({ option_text: o.option_text, is_correct: o.is_correct })),
          });
        } catch (err) { toast.error(formatApiError(err)); }
      })();
    }
  }, [scope, mode, qid]);

  const setOption = (i, patch) => setForm((p) => ({
    ...p, options: p.options.map((o, k) => (k === i ? { ...o, ...patch } : o)),
  }));

  const addOption = () => setForm((p) => ({ ...p, options: [...p.options, { option_text: "", is_correct: false }] }));
  const removeOption = (i) => setForm((p) => ({ ...p, options: p.options.filter((_, k) => k !== i) }));

  const submit = async (asDraft) => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        marks: parseFloat(form.marks) || 1,
        negative_marks: parseFloat(form.negative_marks) || 0,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        status: asDraft ? "draft" : "pending_review",
      };
      // for true_false enforce 2 options
      if (form.question_type === "true_false") {
        payload.options = [
          { option_text: "True", is_correct: form.options[0]?.is_correct ?? false },
          { option_text: "False", is_correct: form.options[1]?.is_correct ?? false },
        ];
      }
      // for descriptive types remove options
      if (["short_answer", "long_answer"].includes(form.question_type)) {
        payload.options = [];
      }
      const url = `/${scope}/question-bank${mode === "edit" ? `/${qid}` : ""}`;
      await (mode === "edit" ? api.put(url, payload) : api.post(url, payload));
      toast.success("Question saved.");
      nav(scope === "admin" ? "/app/super-admin/question-bank" : "/app/faculty/question-bank");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const isObjective = ["single_choice", "multi_select", "true_false"].includes(form.question_type);

  return (
    <DashboardLayout title={mode === "edit" ? "Edit question" : "New question"} breadcrumbs={[scope === "admin" ? "Super Admin" : "Faculty", "Question Bank", mode]}>
      <div className="max-w-4xl space-y-6" data-testid="question-form">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Course *</div>
              <select value={form.course_id} onChange={(e) => setForm((p) => ({ ...p, course_id: e.target.value }))}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="qf-course">
                <option value="">Select course</option>
                {courses.map((c) => <option key={c.id} value={c.id}>{c.course_code} — {c.course_title}</option>)}
              </select>
            </label>
            <label className="block text-sm">
              <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Question type *</div>
              <select value={form.question_type} onChange={(e) => setForm((p) => ({ ...p, question_type: e.target.value }))}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="qf-type">
                {QUESTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </label>
            <label className="block text-sm">
              <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Topic</div>
              <input value={form.topic} onChange={(e) => setForm((p) => ({ ...p, topic: e.target.value }))}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="qf-topic" />
            </label>
            <label className="block text-sm">
              <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Difficulty *</div>
              <select value={form.difficulty_level} onChange={(e) => setForm((p) => ({ ...p, difficulty_level: e.target.value }))}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm">
                {DIFFICULTIES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </label>
            <label className="block text-sm">
              <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Marks</div>
              <input type="number" step="0.5" value={form.marks} onChange={(e) => setForm((p) => ({ ...p, marks: e.target.value }))}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="qf-marks" />
            </label>
            <label className="block text-sm">
              <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Negative marks</div>
              <input type="number" step="0.25" value={form.negative_marks} onChange={(e) => setForm((p) => ({ ...p, negative_marks: e.target.value }))}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
            </label>
          </div>
          <label className="block text-sm">
            <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Question text *</div>
            <textarea rows={3} value={form.question_text} onChange={(e) => setForm((p) => ({ ...p, question_text: e.target.value }))}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="qf-text" />
          </label>
          <label className="block text-sm">
            <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Explanation</div>
            <textarea rows={2} value={form.explanation} onChange={(e) => setForm((p) => ({ ...p, explanation: e.target.value }))}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
          </label>
          <label className="block text-sm">
            <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Tags (comma separated)</div>
            <input value={form.tags} onChange={(e) => setForm((p) => ({ ...p, tags: e.target.value }))}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
          </label>
        </div>

        {isObjective && form.question_type !== "true_false" && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-3">
            <div className="flex items-center justify-between">
              <div className="font-bold text-sm">Options *</div>
              <button type="button" onClick={addOption} className="text-xs text-[#101876] font-bold hover:underline" data-testid="qf-add-option">+ Add option</button>
            </div>
            {form.options.map((opt, i) => (
              <div key={i} className="flex items-start gap-2" data-testid={`qf-option-${i}`}>
                <input type={form.question_type === "single_choice" ? "radio" : "checkbox"}
                  name="correct" checked={opt.is_correct}
                  onChange={(e) => {
                    if (form.question_type === "single_choice") {
                      setForm((p) => ({ ...p, options: p.options.map((o, k) => ({ ...o, is_correct: k === i })) }));
                    } else {
                      setOption(i, { is_correct: e.target.checked });
                    }
                  }}
                  className="mt-2" data-testid={`qf-option-correct-${i}`} />
                <input value={opt.option_text} onChange={(e) => setOption(i, { option_text: e.target.value })}
                  placeholder={`Option ${i + 1}`}
                  className="flex-1 rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid={`qf-option-text-${i}`} />
                {form.options.length > 2 && (
                  <button type="button" onClick={() => removeOption(i)} className="rounded-md border border-slate-200 p-2 text-slate-500 hover:text-rose-600">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
            <div className="text-xs text-slate-500">
              {form.question_type === "single_choice"
                ? "Select exactly ONE correct option."
                : "Multi-select MCQ: mark all correct options."}
            </div>
          </div>
        )}

        {form.question_type === "true_false" && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-3">
            <div className="font-bold text-sm">Correct answer *</div>
            <div className="flex gap-3">
              <label className="flex items-center gap-2 rounded-md border border-slate-200 px-4 py-2 cursor-pointer" data-testid="qf-tf-true">
                <input type="radio" name="tf" checked={form.options[0]?.is_correct}
                  onChange={() => setForm((p) => ({ ...p, options: [{ option_text: "True", is_correct: true }, { option_text: "False", is_correct: false }] }))} />
                <span>True</span>
              </label>
              <label className="flex items-center gap-2 rounded-md border border-slate-200 px-4 py-2 cursor-pointer" data-testid="qf-tf-false">
                <input type="radio" name="tf" checked={form.options[1]?.is_correct}
                  onChange={() => setForm((p) => ({ ...p, options: [{ option_text: "True", is_correct: false }, { option_text: "False", is_correct: true }] }))} />
                <span>False</span>
              </label>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-4">
          <button onClick={() => nav(-1)} className="rounded-md border border-slate-200 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-1">
            <ArrowLeft className="h-3.5 w-3.5" /> Cancel
          </button>
          <div className="flex gap-2">
            <button disabled={saving} onClick={() => submit(true)} className="rounded-md border border-slate-300 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50" data-testid="qf-save-draft">Save as draft</button>
            <button disabled={saving} onClick={() => submit(false)} className="rounded-md bg-[#101876] px-4 py-2 text-xs font-bold text-white hover:bg-[#0c135c]" data-testid="qf-submit-review">
              {scope === "admin" ? "Save (approved via admin)" : "Submit for review"}
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

// =========================================================================
// QUESTION DETAIL
// =========================================================================
export function QuestionDetailPage({ scope = "admin" }) {
  const { qid } = useParams();
  const nav = useNavigate();
  const [q, setQ] = useState(null);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/${scope}/question-bank/${qid}`);
      setQ(data.data.question);
    } catch (err) { toast.error(formatApiError(err)); }
  }, [scope, qid]);
  useEffect(() => { load(); }, [load]);

  const changeStatus = async (status) => {
    try {
      await api.patch(`/admin/question-bank/${qid}/status`, { status, reason: status === "rejected" ? reason : undefined });
      toast.success("Status updated.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submitForReview = async () => {
    try {
      await api.post(`/faculty/question-bank/${qid}/submit-review`);
      toast.success("Submitted for review.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!q) return <DashboardLayout title="Question"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Question detail" breadcrumbs={[scope === "admin" ? "Super Admin" : "Faculty", "Question Bank"]}>
      <div className="max-w-4xl space-y-4" data-testid="question-detail">
        <button onClick={() => nav(-1)} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={q.status} />
            <span className="text-xs font-mono text-slate-500">{q.course_code}</span>
            <span className="text-xs text-slate-500">· {q.topic || "General"}</span>
            <span className="text-xs text-slate-500">· {q.difficulty_level}</span>
            <span className="text-xs text-slate-500">· {q.marks} marks</span>
          </div>
          <div className="text-base font-semibold text-slate-800 whitespace-pre-line">{q.question_text}</div>
          {q.case_scenario && <div className="rounded-md bg-slate-50 p-3 text-sm text-slate-700 whitespace-pre-line">{q.case_scenario}</div>}
          {q.options?.length > 0 && (
            <div className="space-y-2">
              {q.options.map((o) => (
                <div key={o.id} className={`rounded-md border p-3 text-sm flex items-start gap-2 ${o.is_correct ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white"}`}>
                  {o.is_correct ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" /> : <span className="mt-1 h-3.5 w-3.5 rounded-full border-2 border-slate-300" />}
                  <div>{o.option_text}</div>
                </div>
              ))}
            </div>
          )}
          {q.explanation && (
            <div className="rounded-md border-l-2 border-[#329112] bg-emerald-50/40 pl-3 py-2 text-sm text-slate-700">
              <div className="font-bold text-xs uppercase tracking-widest text-emerald-700 mb-1">Explanation</div>
              {q.explanation}
            </div>
          )}
        </div>

        {scope === "admin" ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Approval actions</div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => changeStatus("approved")} className="rounded-md bg-emerald-600 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-700" data-testid="qd-approve">Approve</button>
              <button onClick={() => changeStatus("suspended")} className="rounded-md bg-amber-600 px-4 py-2 text-xs font-bold text-white hover:bg-amber-700" data-testid="qd-suspend">Suspend</button>
              <button onClick={() => changeStatus("archived")} className="rounded-md bg-slate-600 px-4 py-2 text-xs font-bold text-white hover:bg-slate-700">Archive</button>
              <Link to={`/app/super-admin/question-bank/${qid}/edit`} className="rounded-md border border-slate-300 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50">Edit</Link>
            </div>
            <div className="flex items-center gap-2">
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Rejection reason…" className="flex-1 rounded-md border border-slate-200 px-3 py-2 text-sm" />
              <button onClick={() => changeStatus("rejected")} className="rounded-md bg-rose-600 px-4 py-2 text-xs font-bold text-white hover:bg-rose-700" data-testid="qd-reject">Reject</button>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-wrap gap-2">
            {q.status === "draft" && (
              <button onClick={submitForReview} className="rounded-md bg-[#101876] px-4 py-2 text-xs font-bold text-white hover:bg-[#0c135c]" data-testid="qd-submit-review">Submit for review</button>
            )}
            <Link to={`/app/faculty/question-bank/${qid}/edit`} className="rounded-md border border-slate-300 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50">Edit</Link>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

// =========================================================================
// QUESTION IMPORT (course-first, XLSX/CSV, optional exam binding)
// =========================================================================
export function QuestionImportPage({ scope = "admin" }) {
  const [courses, setCourses] = useState([]);
  const [exams, setExams] = useState([]);
  const [courseId, setCourseId] = useState("");
  const [examId, setExamId] = useState("");
  const [fmt, setFmt] = useState("xlsx");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [exporting, setExporting] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      const endpoint = scope === "admin" ? "/admin/courses" : "/faculty/my-courses";
      try {
        const { data } = await api.get(endpoint, {
          // Match the Courses page filter — only "active" (published, non-deleted) courses.
          // Prevents draft/archived/TEST_* junk courses from appearing in the import scope selector.
          params: scope === "admin" ? { page_size: 500, status_filter: "active" } : {},
        });
        setCourses(data.data.courses || []);
      } catch { /* noop */ }
    })();
  }, [scope]);

  useEffect(() => {
    if (!courseId) { setExams([]); setExamId(""); return; }
    (async () => {
      try {
        const { data } = await api.get("/admin/exams", { params: { course_id: courseId, page_size: 200 } });
        setExams(data.data.exams || []);
      } catch { setExams([]); }
    })();
  }, [courseId]);

  const selectedCourse = courses.find((c) => c.id === courseId);

  const downloadTemplate = async () => {
    try {
      const params = { format: fmt };
      if (selectedCourse) params.course_code = selectedCourse.course_code;
      const { data } = await api.get("/admin/question-bank/import/template", {
        params, responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `question_template_${selectedCourse?.course_code || "generic"}.${fmt}`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const downloadWordSample = async () => {
    try {
      const params = { format: "docx" };
      if (selectedCourse) params.course_code = selectedCourse.course_code;
      const { data } = await api.get("/admin/question-bank/import/template", {
        params, responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `sample_question_paper_${selectedCourse?.course_code || "sample"}.docx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const exportQuestions = async () => {
    if (!courseId) { toast.error("Select a course to export."); return; }
    setExporting(true);
    try {
      const { data } = await api.get(`/${scope}/question-bank/export/download`, {
        params: { course_id: courseId, exam_id: examId || undefined, format: fmt },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `questions_${selectedCourse?.course_code || "export"}.${fmt}`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Questions exported.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setExporting(false); }
  };

  const upload = async () => {
    if (!courseId) {
      toast.error("Select a course first — questions are uploaded per course.");
      const el = document.getElementById("qb-step1");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (!file) { toast.error("Choose a file first."); return; }
    setUploading(true);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = new URLSearchParams();
      params.append("course_id", courseId);
      if (examId) params.append("exam_id", examId);
      const { data } = await api.post(
        `/${scope}/question-bank/import/preview?${params.toString()}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setPreview(data.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const confirmImport = async () => {
    if (!preview?.batch_id) return;
    setConfirming(true);
    try {
      const { data } = await api.post(`/${scope}/question-bank/import/confirm?batch_id=${preview.batch_id}`);
      toast.success(`Imported ${data.data.successful_rows} questions.`);
      nav(scope === "admin" ? "/app/super-admin/question-bank" : "/app/faculty/question-bank");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setConfirming(false); }
  };

  return (
    <DashboardLayout title="Import questions" breadcrumbs={[scope === "admin" ? "Super Admin" : "Faculty", "Question Bank", "Import"]}>
      <div className="max-w-4xl space-y-4" data-testid="qb-import">
        {/* Course-first selector */}
        <div className={`rounded-2xl border ${!courseId ? "border-amber-300 ring-2 ring-amber-100" : "border-slate-200"} bg-white p-5 space-y-3`} id="qb-step1">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-sm font-bold uppercase tracking-widest text-slate-500">Step 1 — Course scope *</div>
            {!courseId && (
              <div className="text-xs font-bold text-amber-700 bg-amber-100 rounded-full px-3 py-0.5" data-testid="qb-step1-warning">
                Course is required
              </div>
            )}
          </div>
          <p className="text-xs text-slate-500">
            Bulk uploads are scoped to a course. Select the course first, then optionally attach the questions to a specific exam.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Course *</div>
              <select value={courseId} onChange={(e) => setCourseId(e.target.value)}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="qb-import-course">
                <option value="">Select course</option>
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>{c.course_code} — {c.course_title}</option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Attach to exam (optional)</div>
              <select value={examId} onChange={(e) => setExamId(e.target.value)} disabled={!courseId || exams.length === 0}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm disabled:bg-slate-50" data-testid="qb-import-exam">
                <option value="">— none (course-wide bank) —</option>
                {exams.map((e) => (
                  <option key={e.id} value={e.id}>{e.exam_code} — {e.exam_title}</option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {/* Template + Export */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3">
          <div className="text-sm font-bold uppercase tracking-widest text-slate-500">Step 2 — Template & export</div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="text-xs font-bold text-slate-500">Import format:</div>
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-xs">
              <button onClick={() => setFmt("xlsx")}
                className={`px-3 py-1.5 font-bold ${fmt === "xlsx" ? "bg-[#101876] text-white" : "text-slate-600 hover:bg-slate-50"}`}
                data-testid="qb-fmt-xlsx">XLSX</button>
              <button onClick={() => setFmt("csv")}
                className={`px-3 py-1.5 font-bold ${fmt === "csv" ? "bg-[#101876] text-white" : "text-slate-600 hover:bg-slate-50"}`}
                data-testid="qb-fmt-csv">CSV</button>
            </div>
            <button onClick={downloadTemplate}
              className="rounded-md border border-slate-300 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-1"
              data-testid="qb-download-template">
              <FileText className="h-3.5 w-3.5" /> Download {fmt.toUpperCase()} template{selectedCourse ? ` (${selectedCourse.course_code})` : ""}
            </button>
            <button onClick={downloadWordSample}
              className="rounded-md border border-slate-300 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-1"
              data-testid="qb-download-docx">
              <FileText className="h-3.5 w-3.5" /> Sample paper (Word)
            </button>
            <button onClick={exportQuestions} disabled={!courseId || exporting}
              className="rounded-md border border-slate-300 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-1 disabled:opacity-40"
              data-testid="qb-export">
              <ArrowRight className="h-3.5 w-3.5" /> {exporting ? "Exporting…" : "Export existing questions"}
            </button>
          </div>
          <div className="text-xs text-slate-500">
            The <b>XLSX / CSV</b> templates are for bulk import (structured columns). The
            <b> Sample paper (Word)</b> is a printable question paper with an answer key —
            use it for offline reference or reviews.
          </div>
        </div>

        {/* Upload */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3">
          <div className="text-sm font-bold uppercase tracking-widest text-slate-500">Step 3 — Upload file</div>
          <div className="text-xs text-slate-500">
            Accepted formats: <b>.xlsx</b>, <b>.csv</b>, or <b>.docx</b> (Sample-Paper format —
            <span className="ml-1">Q1., Q2. …</span> with <span>(A) (B) (C) (D)</span> options and an <b>Answer Key</b> section).
          </div>
          <input type="file" accept=".csv,.xlsx,.xls,.docx" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="qb-import-file" />
          <div className="flex items-center gap-3 flex-wrap">
            <button disabled={uploading} onClick={upload}
              className="rounded-md bg-[#101876] px-4 py-2 text-xs font-bold text-white hover:bg-[#0c135c] disabled:opacity-50"
              data-testid="qb-import-preview">
              {uploading ? "Uploading…" : "Preview import"}
            </button>
            {(!courseId || !file) && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5" data-testid="qb-upload-hint">
                {!courseId && !file
                  ? "Select a course in Step 1 and choose a file above."
                  : !courseId
                    ? "Please select a course in Step 1 before uploading."
                    : "Please choose a file above."}
              </div>
            )}
          </div>
        </div>

        {preview && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3" data-testid="qb-import-preview-result">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-md bg-slate-50 p-3 text-center"><div className="text-2xl font-bold">{preview.total}</div><div className="text-xs text-slate-500">Total rows</div></div>
              <div className="rounded-md bg-emerald-50 p-3 text-center"><div className="text-2xl font-bold text-emerald-700">{preview.valid_count}</div><div className="text-xs text-slate-500">Valid</div></div>
              <div className="rounded-md bg-rose-50 p-3 text-center"><div className="text-2xl font-bold text-rose-700">{preview.invalid_count}</div><div className="text-xs text-slate-500">Invalid</div></div>
            </div>
            {preview.invalid_rows?.length > 0 && (
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-rose-700 mb-2">Errors</div>
                <div className="rounded-md border border-rose-200 bg-rose-50/40 max-h-48 overflow-auto">
                  {preview.invalid_rows.map((r) => (
                    <div key={r.row_number} className="border-b border-rose-100 px-3 py-2 text-xs">
                      <div className="font-mono text-rose-700">Row {r.row_number}</div>
                      <div className="text-rose-600">{r.errors.join(" · ")}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {preview.valid_count > 0 && (
              <button disabled={confirming} onClick={confirmImport}
                className="rounded-md bg-emerald-600 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-700" data-testid="qb-import-confirm">
                {confirming ? "Importing…" : `Confirm import (${preview.valid_count} questions)`}
              </button>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
